// Rolling-window throughput estimator. Instantaneous byte deltas are jagged —
// a 200MB part completing in one tick then nothing for seconds reads as either
// "infinitely fast" or "stalled". We keep a short window of (timestamp, bytes)
// samples and derive throughput from the span across the window, which is what
// a human reads as "how fast is this actually going".
export class ThroughputSampler {
  private samples: Array<{ t: number; bytes: number }> = [];
  private readonly windowMs: number;

  constructor(windowMs = 8000) {
    this.windowMs = windowMs;
  }

  // Record the cumulative bytes uploaded so far at time `now`.
  record(bytesUploaded: number, now: number = Date.now()): void {
    this.samples.push({ t: now, bytes: bytesUploaded });
    const cutoff = now - this.windowMs;
    while (this.samples.length > 2 && this.samples[0].t < cutoff) {
      this.samples.shift();
    }
  }

  // Bytes/sec across the retained window, or null until we have a real span.
  bytesPerSecond(): number | null {
    if (this.samples.length < 2) return null;
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const dtSeconds = (last.t - first.t) / 1000;
    if (dtSeconds <= 0) return null;
    const dBytes = last.bytes - first.bytes;
    if (dBytes <= 0) return 0;
    return dBytes / dtSeconds;
  }

  // Seconds remaining to move `totalBytes` given current throughput.
  etaSeconds(bytesUploaded: number, totalBytes: number): number | null {
    const bps = this.bytesPerSecond();
    if (bps === null || bps <= 0) return null;
    const remaining = Math.max(0, totalBytes - bytesUploaded);
    return remaining / bps;
  }

  reset(): void {
    this.samples = [];
  }
}

export function formatBytesPerSecond(bps: number | null): string {
  if (bps === null) return "—";
  if (bps < 1024) return `${Math.round(bps)} B/s`;
  const kb = bps / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB/s`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB/s`;
}

export function formatEta(seconds: number | null): string {
  if (seconds === null || !isFinite(seconds)) return "—";
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
